import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, FileText, Image as ImageIcon, Users, ShoppingBag, Star,
  Mail, Inbox, Settings as SettingsIcon, Menu, Loader2, Send, Download, CreditCard, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const adminNav = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard },
  { label: "Bookings", to: "/admin/bookings", icon: Calendar },
  { label: "Email Log", to: "/admin/emails", icon: Send },
  { label: "Stripe Sync", to: "/admin/stripe/sync", icon: RefreshCw },
  { label: "Stripe Events", to: "/admin/stripe/events", icon: CreditCard },
  { label: "Blog", to: "/admin/blog", icon: FileText },
  { label: "Gallery", to: "/admin/gallery", icon: ImageIcon },
  { label: "Team", to: "/admin/team", icon: Users },
  { label: "Products", to: "/admin/products", icon: ShoppingBag },
  { label: "Testimonials", to: "/admin/testimonials", icon: Star },
  { label: "Subscribers", to: "/admin/subscribers", icon: Mail },
  { label: "Contact", to: "/admin/contact", icon: Inbox },
  { label: "Settings", to: "/admin/settings", icon: SettingsIcon },
  { label: "Import from WP", to: "/admin/wp-import", icon: Download },
];

function Sidebar({ open, toggle }: { open: boolean; toggle: () => void }) {
  const location = useLocation();
  return (
    <>
      <button onClick={toggle} className="lg:hidden fixed top-20 left-4 z-40 bg-panel border border-border p-2">
        <Menu className="w-5 h-5" />
      </button>
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-surface border-r border-border z-30 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <nav className="p-4 space-y-1 mt-4">
          {adminNav.map((item) => (
            <Link key={item.to} to={item.to}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-body transition-colors ${
                location.pathname === item.to ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-panel"
              }`}>
              <item.icon className="w-4 h-4" />{item.label}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
}

type EventRow = {
  id: string;
  stripe_event_id: string;
  type: string;
  livemode: boolean;
  status: string;
  error: string | null;
  related_type: string | null;
  related_id: string | null;
  payload: unknown;
  received_at: string;
  processed_at: string | null;
};

const PAGE_SIZE = 50;

export default function AdminStripeEvents() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rows, setRows] = useState<EventRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [selected, setSelected] = useState<EventRow | null>(null);
  const [retrying, setRetrying] = useState(false);

  const load = async () => {
    setLoading(true);
    let q = supabase.from("stripe_webhook_events")
      .select("*", { count: "exact" })
      .order("received_at", { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
    if (typeFilter.trim()) q = q.ilike("type", `%${typeFilter.trim()}%`);
    if (statusFilter !== "all") q = q.eq("status", statusFilter);
    if (modeFilter !== "all") q = q.eq("livemode", modeFilter === "live");
    const { data, count, error } = await q;
    if (error) toast.error(error.message);
    setRows((data ?? []) as unknown as EventRow[]);
    setTotal(count ?? 0);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, typeFilter, statusFilter, modeFilter]);

  const retry = async (rowId: string) => {
    setRetrying(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-event-retry", { body: { event_row_id: rowId } });
      if (error) throw error;
      toast.success(`Retry: ${data?.status ?? "done"}`);
      await load();
      setSelected(null);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRetrying(false);
    }
  };

  const statusBadge = (s: string) => {
    const map: Record<string, string> = {
      processed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
      received: "bg-sky-500/15 text-sky-400 border-sky-500/30",
      failed: "bg-red-500/15 text-red-400 border-red-500/30",
      ignored: "bg-muted text-muted-foreground border-border",
    };
    return <span className={`px-2 py-0.5 text-[10px] font-mono uppercase border ${map[s] ?? "border-border"}`}>{s}</span>;
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="pt-16 min-h-screen">
      <Sidebar open={sidebarOpen} toggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="lg:ml-64 p-6 lg:p-10">
        <h1 className="text-3xl font-display tracking-widest mb-6">STRIPE WEBHOOK EVENTS</h1>

        <div className="flex flex-wrap gap-3 mb-6">
          <Input
            placeholder="Filter by type (e.g. payment_intent)"
            value={typeFilter}
            onChange={(e) => { setPage(0); setTypeFilter(e.target.value); }}
            className="max-w-xs"
          />
          <Select value={statusFilter} onValueChange={(v) => { setPage(0); setStatusFilter(v); }}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="processed">Processed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="ignored">Ignored</SelectItem>
            </SelectContent>
          </Select>
          <Select value={modeFilter} onValueChange={(v) => { setPage(0); setModeFilter(v); }}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modes</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="test">Test</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>

        <div className="bg-panel border border-border">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground font-body text-sm">No webhook events yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr className="text-left text-xs font-mono uppercase text-muted-foreground">
                    <th className="px-4 py-3">Received</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Mode</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Related</th>
                    <th className="px-4 py-3">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}
                      className="border-b border-border last:border-0 hover:bg-surface cursor-pointer"
                      onClick={() => setSelected(r)}>
                      <td className="px-4 py-3 font-mono text-xs">{new Date(r.received_at).toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.type}</td>
                      <td className="px-4 py-3">
                        <Badge variant={r.livemode ? "default" : "secondary"}>{r.livemode ? "LIVE" : "TEST"}</Badge>
                      </td>
                      <td className="px-4 py-3">{statusBadge(r.status)}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {r.related_type ? `${r.related_type}:${r.related_id?.slice(0, 8)}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-red-400 max-w-xs truncate">{r.error ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-4 text-sm font-mono">
          <span className="text-muted-foreground">{total} events · page {page + 1} / {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>

        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-mono text-sm break-all">{selected?.type}</SheetTitle>
            </SheetHeader>
            {selected && (
              <div className="mt-4 space-y-4 text-xs font-mono">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">Event ID:</span> <br />{selected.stripe_event_id}</div>
                  <div><span className="text-muted-foreground">Status:</span> {statusBadge(selected.status)}</div>
                  <div><span className="text-muted-foreground">Mode:</span> {selected.livemode ? "LIVE" : "TEST"}</div>
                  <div><span className="text-muted-foreground">Processed:</span> {selected.processed_at ? new Date(selected.processed_at).toLocaleString() : "—"}</div>
                </div>
                {selected.error && <div className="text-red-400 break-words">Error: {selected.error}</div>}
                <Button onClick={() => retry(selected.id)} disabled={retrying}>
                  {retrying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Retry processing
                </Button>
                <pre className="bg-surface border border-border p-4 overflow-auto max-h-[60vh] text-[10px]">
                  {JSON.stringify(selected.payload, null, 2)}
                </pre>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}